import classNames from "classnames";
import { useMemo, useState, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Skeleton } from "antd";
import "react-lazy-load-image-component/src/effects/blur.css";

const ImgSkeleton = ({ width, proportion, active, withIcon = false }) => {
  return (
    <div>
      {withIcon ? (
        <Skeleton.Image
          style={{ width, height: width * (proportion / 100) }}
          active={active}
        />
      ) : (
        <Skeleton.Button
          style={{ width, height: width * (proportion / 100) }}
          active={active}
        />
      )}
    </div>
  );
};

export const TableCellPicture = ({
  pageId,
  cacheKey,
  width = 150,
  proportion = 100,
  imgSrcForced,
  withCache = true,
}) => {
  const [loadingError, setLoadingError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setLoadingError(null);
  }, [pageId, cacheKey, imgSrcForced]);

  const imgSrc = useMemo(() => {
    const base = imgSrcForced
      ? imgSrcForced
      : `${process.env.REACT_APP_DEV_URL || document.location.origin}/api/v1/pages/${pageId}/icon-mini`;

    if (!cacheKey) return base;

    const encoded = encodeURIComponent(cacheKey);
    return base.includes("?") ? `${base}&v=${encoded}` : `${base}?v=${encoded}`;
  }, [imgSrcForced, pageId, cacheKey]);

  const onError = (e) => {
    setLoadingError(e);
    setIsLoading(false);
  };

  const onLoad = () => {
    setIsLoading(false);
    setLoadingError(null);
  };

  if (loadingError) {
    return (
      <div className={classNames("lf-table-cell-picture")}>
        <div className={classNames("lf-table-cell-picture__inside")}>
          <ImgSkeleton width={width} proportion={proportion} withIcon />
        </div>
      </div>
    );
  }

  const ImgNode = withCache ? LazyLoadImage : "img";

  return (
    <div style={{ width }} className={classNames("lf-table-cell-picture")}>
      <div
        className={classNames("lf-table-cell-picture__inside")}
        style={{
          position: "relative",
          maxWidth: width,
          minWidth: width,
          height: width * (proportion / 100),
          maxHeight: width * (proportion / 100),
          overflow: "hidden",
          borderRadius: "8px",
        }}
      >
        <ImgNode
          className={classNames("lf-table-cell-picture__img")}
          key={`${pageId}-${cacheKey || "no-cache-key"}`}
          src={imgSrc}
          loading="lazy"
          style={{
            maxWidth: width,
            minWidth: width,
            height: width * (proportion / 100),
            maxHeight: width * (proportion / 100),
            objectFit: "cover",
            scale: "1.09",
          }}
          onError={onError}
          onLoad={onLoad}
        />

        {isLoading && (
          <div
            style={{
              position: "absolute",
              zIndex: 10,
              top: 0,
              left: 0,
              width: width,
              height: width * (proportion / 100),
              background: "white",
            }}
          >
            <ImgSkeleton width={width} proportion={proportion} active />
          </div>
        )}
      </div>
    </div>
  );
};